module Sm.Service.Remote

open System
open Fable.Core
open Fable.Core.JsInterop
open FsToolkit.ErrorHandling

open Browser.Navigator
open MobF
open Pocketbase

open Sm.Core.Text
open Sm.Core.Interop
open Sm.Core.Interop.Operators
open Sm.Core.Option.Operators
open Sm.Core.Timer

open Sm.Service.App
open Sm.Service.Storage
open Sm.Service.Repository

type MessageHeader = {
    Account: string
    Client: Guid
    ClockFrom: int64
    ClockTo: int64
}

type Message = {
    Header: MessageHeader
    Updates: Update list
}

module Message =
    let internal guid (x: string) = Guid.Parse(x)

    let parseHeader msg =
        option {
            let! account = msg @ "account" <!> string
            let! client = msg @ "client" <!> string <!> guid
            let! clockFrom = msg @ "clock_from" <!> LogicalClock.parse
            let! clockTo = msg @ "clock_to" <!> LogicalClock.parse

            return {
                Account = account
                Client = client
                ClockFrom = clockFrom
                ClockTo = clockTo
            }
        }

    let parse msg =
        option {
            let! header = parseHeader msg

            return {
                Header = header
                Updates =
                    msg @.. "updates"
                    |> Seq.choose (fun record ->
                        option {
                            let! id = record @ "record" <!> string <!> guid
                            let! field = record @ "name" <!> string
                            let value = record @ "value" <!> string >>= nonempty
                            let! clock = record @ "clock" <!> LogicalClock.parse

                            return {
                                Clock = clock
                                Client = header.Client
                                Target = {
                                    Id = id
                                    Field = field
                                    Value = value
                                }
                            }
                        }
                    )
                    |> List.ofSeq
            }
        }

    let serialize msg =
        createObj [
            "account" ==> string msg.Header.Account
            "client" ==> string msg.Header.Client
            "clock_from" ==> LogicalClock.serialize msg.Header.ClockFrom
            "clock_to" ==> LogicalClock.serialize msg.Header.ClockTo
            "updates" ==> [|
                for update in msg.Updates do
                    createObj [
                        "record" ==> string update.Target.Id
                        "name" ==> update.Target.Field
                        "value" ==> update.Target.Value
                        "clock" ==> LogicalClock.serialize update.Clock
                    ]
            |]
        ]

    let validateChain messages clock =
        messages
        |> Seq.groupBy _.Header.Client
        |> Seq.map (fun (client, messages) ->
            let start, isSequential =
                match List.ofSeq messages with
                | [] -> 0L, false
                | [single] -> single.Header.ClockFrom, true
                | _ ->
                    let chain =
                        messages
                        |> Seq.map (fun m -> m.Header.ClockFrom, m.Header.ClockTo)
                        |> Seq.sort
                        |> Seq.pairwise
                        |> Seq.cache

                    let isSequential =
                        chain
                        |> Seq.forall (fun ((_, a), (b, _)) -> a = b)

                    let first =
                        chain
                        |> Seq.map (fst >> fst)
                        |> Seq.tryHead
                        |> Option.defaultValue 0

                    first, isSequential

            match clock.Remote.TryFind(client) with
            | None -> isSequential
            | Some previous ->
                let isContinuation = previous = start
                isContinuation && isSequential
        )
        |> Seq.forall id

type ClockResult = {
    Clock: int64
}

type ReceiveResult = {
    TotalCount: int
    HasMore: bool
    Messages: Message list
}

type ISyncSubscription =
    abstract ServerChanged: IEvent<unit>
    abstract Stop: unit -> JS.Promise<unit>

type ISyncServer =
    abstract Clock: unit -> JS.Promise<ClockResult>
    abstract Receive:  Map<Guid, int64> -> JS.Promise<ReceiveResult>
    abstract Send: Message -> JS.Promise<ClockResult>
    abstract Subscribe: unit -> JS.Promise<ISyncSubscription>

type PocketbaseSyncServer(clientId: Guid, client: Client) =
    let syncCollection = client.collection("sync")
    let clockCollection = client.collection("clock")

    let clockFail () =
        failwithf "Unable to read server clock for %A" clientId

    let parseClock response =
        match response @ "clock_to" <!> BigInt.create with
        | None -> clockFail ()
        | Some x -> { Clock = x }

    let readStatus (e: exn) =
        match e @ "status" with
        | JsNumber status -> Some (int status)
        | _ -> None

    let validateToken () =
        match User.current client with
        | Some _ -> ()
        | None -> failwith "You are not signed in, or your session has expired"

    interface ISyncServer with
        member _.Clock() = promise {
            validateToken ()

            try
                let filter = client.filter("client = {:id}", {| id = clientId |})
                let options = jsOptions<RecordListOptions>(fun q ->
                    q.fields <- Some "clock_to"
                )

                let! response = clockCollection.getFirstListItem(filter, options)

                match response with
                | None -> return clockFail ()
                | Some response -> return response |> parseClock

            with e ->
                match readStatus e with
                | Some 404 -> return { Clock = 0 }
                | _ -> return raise e
        }

        member _.Receive(remoteClocks) = promise {
            validateToken ()

            let filter =
                ([$"""(client != "{clientId}")"""], remoteClocks)
                ||> Map.fold (fun parts client clock ->
                    $"""(client != "{client}" || clock_to > {clock})""" :: parts
                )
                |> join " && "
                |> Some

            let options = jsOptions<FullListOptions> (fun q ->
                q.sort <- Some "clock_to"
                q.filter <- filter
                q.fields <- Some "account,client,clock_from,clock_to,updates"
            )

            let! response = syncCollection.getList(page = 1, perPage = 20, options = options)

            return {
                TotalCount = int response.totalItems
                HasMore = response.totalItems - response.perPage > 0
                Messages =
                    response.items
                    |> Seq.choose Message.parse
                    |> Seq.toList
            }
        }

        member _.Send(message) = promise {
            validateToken ()

            let options = jsOptions<ListOptions>(fun q ->
                q.fields <- Some "clock_to"
            )

            let body = Message.serialize message
            let! result = syncCollection.create<obj>(!!body, options)

            return parseClock result
        }

        member _.Subscribe() = promise {
            validateToken ()

            let serverChanged = Event<unit>()

            let! unsub = syncCollection.subscribe("*", fun e ->
                if e.action = "create" then
                    match Message.parseHeader e.record with
                    | Some { Client = client } when clientId = client -> ()
                    | _ -> serverChanged.Trigger()
            )

            return { new ISyncSubscription with
                member _.ServerChanged = serverChanged.Publish
                member _.Stop() = unsub.Invoke()
            }
        }

module Synchronizer =
    type Progress =
        | Preparing
        | Sending of current: int * total: int
        | Receiving of current: int * total: int

    type IListener =
        abstract Stop: unit -> JS.Promise<unit>

    type State = {
        Progress: Progress option
        Retries: int
        Error: (exn * DateTime) option
        ServerClock: int64 option
        Listener: IListener option
        BusyNotification: bool
        Rounds: int
        LastFullSync: DateTime option
    }

    type Step =
        | Receive
        | EstablishClock
        | Send of int64
        | Complete of int64

    type Msg =
        | Run of Step
        | Report of Progress
        | HandleError of exn
        | CompleteRound
        | HalfSync
        | FullSync
        | Start
        | Listen of IListener
        | Stop
        | Restart

    type Status =
        | Offline
        | Pending
        | UpToDate of DateTime
        | Running of Progress
        | HaltedSince of DateTime * string
        | Synchronized

    type IComputed =
        abstract Status: Status

    type SynchronizerType = Model<State, Msg, IComputed>

    type Options = {
        RetryCount: int
        SendBatchSize: int
        InitialSyncDelay: int
        MergeDebounce: int
        RetryDelay: int
    } 
    with
        static member Default =
            {
                RetryCount = 5
                SendBatchSize = 200
                InitialSyncDelay = 1000
                MergeDebounce = 1000
                RetryDelay = 1000
            }

    let create options env (server: ISyncServer) (repository: Repository): SynchronizerType =
        let localMerged =
            repository.Merged
            |> Event.filter (fun x -> x.Update.Client = env.ClientId)

        let init () =
            {
                Progress = None
                Retries = 0
                Error = None
                ServerClock = None
                Listener = None
                BusyNotification = false
                Rounds = 0
                LastFullSync = None
            }

        let update (state, post, computed: IComputed) msg =
            let offline = match computed.Status with Offline -> true | _ -> false
            let halted = match computed.Status with HaltedSince _ -> true | _ -> false
            let running = match computed.Status with Running _ -> true | _ -> false

            let run compensation (p: JS.Promise<unit>) =
                p
                |> Promise.catchBind (fun e -> promise {
                    post (HandleError e)
                    do! Promise.sleep options.RetryDelay
                    compensation |> Option.iter post
                })
                |> Promise.start

            let exec promise = run None promise
            let retry msg promise = run (Some msg) promise

            match msg with
            | Run _ when halted || offline ->
                state

            | Run Receive ->
                retry msg (promise {
                    let! result = server.Receive(repository.Clock.Remote)

                    if not (Message.validateChain result.Messages repository.Clock) then
                        failwith "Some messages do not form a valid chain"
                    else
                        let total, current =
                            match state.Progress with
                            | Some (Receiving (total = t; current = c)) -> t, c + result.Messages.Length
                            | _ -> int result.TotalCount, result.Messages.Length

                        let progress = Receiving (total = total, current = current)
                        post (Report progress)

                        do! repository.Merge(
                            result.Messages
                            |> Seq.collect (fun x -> x.Updates)
                        )

                        do! repository.UpdateClocks(
                            result.Messages
                            |> Seq.map (fun x -> x.Header.Client, x.Header.ClockTo)
                        )

                        if result.HasMore then
                            post (Run Receive)
                        else
                            post (Run EstablishClock)
                })
                state

            | Run EstablishClock ->
                match state.ServerClock with
                | Some clock ->
                    let nextStep = Send clock
                    post (Run nextStep)
                | None ->
                    retry msg (promise {
                        let! result = server.Clock()
                        let nextStep = Send result.Clock
                        post (Run nextStep)
                    })
                state

            | Run (Send clock) ->
                retry (Run EstablishClock) (promise {
                    let! batch = repository.QueryLocal(clock, options.SendBatchSize)

                    if batch.Updates.Length > 0 then
                        let total, current =
                            match state.Progress with
                            | Some (Sending (total = t; current = c)) -> t, c + batch.BatchCount
                            | _ -> batch.TotalCount, batch.BatchCount

                        let progress = Sending (total = total, current = current)
                        post (Report progress)

                        let msg = {
                            Header = {
                                Account = env.AccountId
                                Client = env.ClientId
                                ClockFrom = clock
                                ClockTo = batch.Clock
                            }
                            Updates = batch.Updates
                        }

                        let! result = server.Send(msg)
                        let nextStep = Send result.Clock
                        post (Run nextStep)
                    else
                        post (Run (Complete clock))
                })
                state

            | Run (Complete clock) ->
                post CompleteRound

                if state.BusyNotification then
                    post FullSync

                { state with ServerClock = Some clock }

            | Report progress ->
                { state with Progress = Some progress }

            | CompleteRound ->
                { state with Rounds = state.Rounds + 1; Progress = None }

            | HandleError e ->
                Browser.Dom.console.error("Sync error (%i retries);", state.Retries, e)

                let retries = state.Retries + 1
                if retries > options.RetryCount then
                    post CompleteRound
                    { state with Error = Some (e, DateTime.Now); ServerClock = None }
                else
                    { state with Retries = retries }

            | HalfSync | FullSync when running ->
                { state with BusyNotification = true }

            | HalfSync ->
                post (Run EstablishClock)
                { state with Progress = Some Preparing }

            | FullSync ->
                post (Run Receive)
                { state with Progress = Some Preparing; BusyNotification = false; LastFullSync = Some DateTime.Now }

            | Start when state.Listener.IsNone ->
                exec (promise {
                    let scheduleHalfSync = debounce options.MergeDebounce (fun () -> post HalfSync)
                    let onMerge = Handler<MergeEvent>(fun _ _ -> scheduleHalfSync ())

                    localMerged.AddHandler(onMerge)

                    let! subscription = server.Subscribe()
                    subscription.ServerChanged.Add(fun () -> post FullSync)

                    let listener =
                        { new IListener with
                            member _.Stop() = promise {
                                localMerged.RemoveHandler(onMerge)
                                do! subscription.Stop()
                            }
                        }

                    post (Listen listener)
                    deferFor options.InitialSyncDelay (fun () -> post FullSync)
                })
                state

            | Start ->
                state

            | Listen listener ->
                { state with Listener = Some listener }

            | Stop ->
                match state.Listener with
                | None -> state
                | Some listener ->
                    exec (listener.Stop())
                    { state with Listener = None; ServerClock = None }

            | Restart ->
                post Stop
                post Start
                post FullSync
                { state with Error = None; ServerClock = None; Progress = None; Retries = 0 }

        let compute state =
            { new IComputed with
                member _.Status =
                    match state with
                    | { Error = Some (e, d) } -> HaltedSince (d, e.Message)
                    | { Progress = Some p } -> Running p
                    | { ServerClock = Some _ } -> Synchronized
                    | { Listener = Some _ } ->
                        match state.LastFullSync with
                        | None -> Pending
                        | Some d -> UpToDate d
                    | _ -> Offline
            }

        Model.useInit init
        |> Model.andUpdateWithComputed update
        |> Model.createWithComputed compute

type ErrorReporter(env, client: Client) =
    let mutable sub = None
    let collection = client.collection("errors")

    let send (scope: string, ex: exn) =
        try
            if navigator.onLine then
                let msg = createObj [
                    "scope" ==> scope
                    "message" ==> ex.Message
                    "detail" ==> ex.StackTrace
                    "user" ==> env.AccountId
                ]

                let bodyParams: U2<_, Browser.Types.FormData> = U2.Case1 (Some msg)
                collection.create(bodyParams, createEmpty<RecordOptions>)
                |> Promise.start
        with _ ->
            ()

    member this.Start() =
        this.Stop()

        let d =
            CriticalError.instance |> Subscribe.manual
                (fun x -> x.Error)
                (fun error -> error |> Option.iter send)

        sub <- Some d

    member _.Stop() =
        match sub with
        | None -> ()
        | Some s -> s.Dispose()

        sub <- None
